/**
 * Answered-so-far list — inline edit on row; #344 icon actions (no text Edit/Answer).
 */
import { formatMoneyInputBlur } from '../../../../lib/formatters'
import { isOverheadCostDescription } from '../../../../lib/costsMarginOverhead'
import type { AnsweredState, CostsMarginPayload, JobCostRow } from '../../../../api/costsMargin'
import type { GuidedQuestion } from '../../../../lib/costsMarginQuestions'
import AnsweredRow, { fmtCents } from './AnsweredRow'
import type { QuestionSavePayload } from './types'

export default function AnsweredList({
  questions,
  margin,
  costs,
  answered,
  skippedIds = [],
  busy,
  onSave,
}: {
  questions: GuidedQuestion[]
  margin: CostsMarginPayload | undefined
  costs: JobCostRow[]
  answered: AnsweredState | undefined
  skippedIds?: string[]
  busy: boolean
  onSave: (payload: QuestionSavePayload) => Promise<void>
}) {
  const skipped = new Set(skippedIds)
  const laborRows = costs.filter((c) => c.category === 'labor')
  const laborCents = laborRows.reduce((s, c) => s + c.amount_cents, 0)
  const overheadCosts = costs.filter((c) => isOverheadCostDescription(c.description))
  const overheadCents = overheadCosts.reduce((s, c) => s + c.amount_cents, 0)
  const otherCount = costs.filter((c) => c.category !== 'labor' && !overheadCosts.includes(c)).length
  const subRows = answered?.subcontract_amounts ?? []
  const subById = new Map(subRows.map((s) => [s.project_vendor_id, s]))
  const subCoCount = answered?.sub_change_orders?.count
    ?? margin?.change_orders?.sub?.length
    ?? 0
  const subCoCents = (margin?.change_orders?.sub ?? [])
    .filter((co) => co.status !== 'void' && co.status !== 'voided')
    .reduce((s, co) => s + Math.round((co.amount || 0) * 100), 0)
  const subOptions = (margin?.commitments ?? []).map((c) => ({
    id: c.project_vendor_id,
    label: c.company_name || c.trade || `Sub #${c.project_vendor_id}`,
  }))

  return (
    <section className="cm-card" data-testid="cm-answered-list">
      <div className="cm-section-title">
        <h3>Answered so far</h3>
        <span>Hover a row to edit</span>
      </div>
      <div className="cm-answer-list">
        {questions.map((q) => {
          const forceNot = skipped.has(q.id)
          if (q.kind === 'contract') {
            return (
              <AnsweredRow
                key={q.id}
                question={q}
                label="Your contract with the owner"
                subline="Prefilled from the project · display only"
                answered
                value={margin?.revised_contract?.formatted ?? fmtCents(0)}
                editable={false}
                busy={busy}
                onSave={onSave}
              />
            )
          }
          if (q.kind === 'subcontract_amount' && q.projectVendorId != null) {
            const s = subById.get(q.projectVendorId)
            const has = !forceNot && (s?.answered === true || (s?.subcontract_amount_cents || 0) > 0)
            const cents = s?.subcontract_amount_cents || 0
            return (
              <AnsweredRow
                key={q.id}
                question={q}
                label={`${q.vendorName} · Subcontract Amount`}
                subline={s ? `Invoiced ${fmtCents(s.invoiced_cents)} · paid ${fmtCents(s.paid_cents)}` : undefined}
                answered={has}
                value={has ? fmtCents(cents) : undefined}
                editable
                prefill={cents > 0 ? { amount: formatMoneyInputBlur(String(cents / 100)) } : { amount: '' }}
                busy={busy}
                onSave={onSave}
              />
            )
          }
          if (q.kind === 'sub_change_orders') {
            const has = !forceNot && (answered?.sub_change_orders?.answered === true || subCoCount > 0)
            return (
              <AnsweredRow
                key={q.id}
                question={q}
                label="Sub change orders"
                subline={has ? `${subCoCount} change order${subCoCount === 1 ? '' : 's'}` : undefined}
                answered={has}
                value={has ? fmtCents(subCoCents) : undefined}
                editable
                prefill={{ amount: '' }}
                busy={busy}
                subOptions={subOptions}
                onSave={onSave}
              />
            )
          }
          if (q.kind === 'labor') {
            const has = !forceNot && (answered?.labor?.answered === true || laborCents > 0)
            return (
              <AnsweredRow
                key={q.id}
                question={q}
                label="Your own labor on this job"
                answered={has}
                value={has ? fmtCents(laborCents) : undefined}
                editable
                prefill={
                  laborCents > 0
                    ? { amount: formatMoneyInputBlur(String(laborCents / 100)), text: laborRows[0]?.description || 'Own labor' }
                    : { amount: '', text: 'Own labor' }
                }
                busy={busy}
                onSave={onSave}
              />
            )
          }
          if (q.kind === 'overhead') {
            const has = !forceNot && (answered?.overhead?.answered === true || overheadCents > 0)
            return (
              <AnsweredRow
                key={q.id}
                question={q}
                label="Overhead / general conditions"
                answered={has}
                value={has ? fmtCents(overheadCents) : undefined}
                editable
                prefill={
                  overheadCents > 0
                    ? {
                        amount: formatMoneyInputBlur(String(overheadCents / 100)),
                        text: overheadCosts[0]?.description || 'Overhead / general conditions',
                      }
                    : { amount: '', text: 'Overhead / general conditions' }
                }
                busy={busy}
                onSave={onSave}
              />
            )
          }
          if (q.kind === 'other_costs') {
            const has = !forceNot && otherCount > 0
            return (
              <AnsweredRow
                key={q.id}
                question={q}
                label="Other costs"
                answered={has}
                value={has ? `${otherCount} cost${otherCount === 1 ? '' : 's'} entered` : undefined}
                editable
                prefill={{ amount: '' }}
                busy={busy}
                onSave={onSave}
              />
            )
          }
          return null
        })}
      </div>
    </section>
  )
}
